import React from "react";
import { Modal, Button, Alert, Spin, Tag } from "antd";
import {
  AudioOutlined,
  LoadingOutlined,
  CheckOutlined,
  CloseOutlined,
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  RedoOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  useVoiceShift,
  VoiceShiftState,
  SingleShift,
  MultiShift,
  LeaveShift,
  ResolvedShift,
  DeletableShift,
} from "./useVoiceShift";
import { format, parseISO } from "date-fns";
import "./VoiceShiftModal.scss";

interface Props {
  clientId: string;
  onShiftCreated: () => void;
  onClose: () => void;
}

export default function VoiceShiftModal({
  clientId,
  onShiftCreated,
  onClose,
}: Props) {
  const {
    state,
    lang,
    setLang,
    startListening,
    stopListening,
    resolveDisambiguation,
    confirmShift,
    confirmDelete,
    reset,
  } = useVoiceShift(clientId);

  const handleConfirm = async () => {
    if (state.status !== "preview") return;
    const success = await confirmShift(state.shift);
    if (success) {
      onShiftCreated();
      onClose();
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDelete = async () => {
    if (state.status !== "delete-preview") return;
    const success = await confirmDelete(state.shifts);
    if (success) {
      onShiftCreated();
      onClose();
    }
  };

  const renderContent = (s: VoiceShiftState) => {
    switch (s.status) {
      case "idle":
        return (
          <div className="vsm-idle">
            <p className="vsm-hint">Press the mic and say something like:</p>
            <ul className="vsm-examples">
              <li>"Dodaj dnevnicu za Marko sutra u 8"</li>
              <li>"Napravi smene za Jovana ovu nedelju od 8 do 16"</li>
              <li>"Noćna smena za Nikola danas u Panorama"</li>
              <li>"Godišnji odmor za Ana od ponedeljka do petka"</li>
              <li>"Obriši smenu za Marka sutra"</li>
              <li>"Izbriši dnevnice za Nevenu od 23 do 27"</li>
            </ul>
            <div className="vsm-lang-toggle">
              <button
                className={`vsm-lang-btn ${lang === "bcms" ? "vsm-lang-active" : ""}`}
                onClick={() => setLang("bcms")}
              >
                SRB/BA/HR
              </button>
              <button
                className={`vsm-lang-btn ${lang === "sl" ? "vsm-lang-active" : ""}`}
                onClick={() => setLang("sl")}
              >
                SLO
              </button>
              <button
                className={`vsm-lang-btn ${lang === "en" ? "vsm-lang-active" : ""}`}
                onClick={() => setLang("en")}
              >
                EN
              </button>
            </div>

            <button className="vsm-mic-btn" onClick={startListening}>
              <AudioOutlined />
              Počni snimanje
            </button>
          </div>
        );

      case "listening":
        return (
          <div className="vsm-listening">
            <div className="vsm-lang-toggle">
              <button
                className={`vsm-lang-btn ${lang === "bcms" ? "vsm-lang-active" : ""}`}
                onClick={() => setLang("bcms")}
              >
                SRB/BA/HR
              </button>
              <button
                className={`vsm-lang-btn ${lang === "sl" ? "vsm-lang-active" : ""}`}
                onClick={() => setLang("sl")}
              >
                SLO
              </button>
              <button
                className={`vsm-lang-btn ${lang === "en" ? "vsm-lang-active" : ""}`}
                onClick={() => setLang("en")}
              >
                EN
              </button>
            </div>
            <div className="vsm-pulse-ring">
              <div className="vsm-pulse-dot">
                <AudioOutlined />
              </div>
            </div>
            <p className="vsm-listening-label">
              Slušam… govorite pa pritisnite Gotovo
            </p>
            <div className="vsm-live-transcript">
              {s.interimTranscript || "…"}
            </div>
            <Button
              size="large"
              type="primary"
              className="vsm-done-btn"
              onClick={stopListening}
            >
              Gotovo
            </Button>
          </div>
        );

      case "parsing":
        return (
          <div className="vsm-parsing">
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
            />
            <p>Processing: "{s.transcript}"</p>
          </div>
        );

      case "preview":
        return (
          <div className="vsm-preview">
            {s.ambiguities.length > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={s.ambiguities.join(" · ")}
              />
            )}
            <ShiftPreviewCard shift={s.shift} />
            <div className="vsm-actions">
              <Button icon={<RedoOutlined />} onClick={startListening}>
                Record Again
              </Button>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleConfirm}
              >
                {s.shift.isMultipleShift ? "Create Shifts" : "Create Shift"}
              </Button>
            </div>
          </div>
        );

      case "disambiguation":
        return (
          <div className="vsm-disambiguation">
            <p>Multiple employees found. Who did you mean?</p>
            <div className="vsm-dis-options">
              {s.options.map((opt) => (
                <button
                  key={String(opt._id)}
                  className="vsm-dis-btn"
                  onClick={() => resolveDisambiguation(opt, s.partial)}
                >
                  {opt.firstName} {opt.lastName}
                </button>
              ))}
            </div>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => reset()}
              style={{ marginTop: 8 }}
            >
              Cancel
            </Button>
          </div>
        );

      case "saving":
        return (
          <div className="vsm-parsing">
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
            />
            <p>Creating shift…</p>
          </div>
        );

      case "delete-preview":
        return (
          <div className="vsm-delete-preview">
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                <>
                  <strong>{s.shifts.length}</strong> shift
                  {s.shifts.length !== 1 ? "s" : ""} will be deleted for{" "}
                  <strong>{s.employeeFullName}</strong>
                </>
              }
            />
            <div className="vsm-delete-list">
              {s.shifts.map((shift) => (
                <div key={String(shift._id)} className="vsm-delete-item">
                  <span className="vsm-delete-date">
                    {formatDate(shift.date)}
                  </span>
                  <span className="vsm-delete-time">
                    {shift.startTime} – {shift.endTime}
                  </span>
                  <ShiftTypeTag type={shift.shiftType} />
                </div>
              ))}
            </div>
            <div className="vsm-actions" style={{ marginTop: 16 }}>
              <Button icon={<RedoOutlined />} onClick={startListening}>
                Record Again
              </Button>
              <Button icon={<CloseOutlined />} onClick={() => reset()}>
                Cancel
              </Button>
              <Button
                danger
                type="primary"
                icon={<DeleteOutlined />}
                onClick={handleDelete}
              >
                Delete {s.shifts.length} Shift{s.shifts.length !== 1 ? "s" : ""}
              </Button>
            </div>
          </div>
        );

      case "deleting":
        return (
          <div className="vsm-parsing">
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
            />
            <p>Deleting shifts…</p>
          </div>
        );

      case "error":
        return (
          <div className="vsm-error">
            <Alert type="error" showIcon message={s.message} />
            <div className="vsm-actions" style={{ marginTop: 16 }}>
              <Button icon={<CloseOutlined />} onClick={() => reset()}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={startListening}
                icon={<AudioOutlined />}
              >
                Try Again
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <Modal
      open
      title="Voice Shift"
      footer={null}
      onCancel={handleClose}
      width={480}
      className="voice-shift-modal"
      wrapClassName="voice-shift-modal-wrap"
      destroyOnClose
      style={{ maxWidth: "100vw" }}
    >
      {renderContent(state)}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Preview card — adapts to single / multi / leave
// ---------------------------------------------------------------------------
function ShiftPreviewCard({ shift }: { shift: ResolvedShift }) {
  if (shift.shiftType === "LEAVES") {
    return <LeavePreviewCard shift={shift as LeaveShift} />;
  }
  if (shift.isMultipleShift) {
    return <MultiPreviewCard shift={shift as MultiShift} />;
  }
  return <SinglePreviewCard shift={shift as SingleShift} />;
}

function SinglePreviewCard({ shift }: { shift: SingleShift }) {
  const formattedDate = formatDate(shift.date);
  return (
    <div className="vsm-card">
      <CardRow
        icon={<UserOutlined />}
        label="Employee"
        value={shift.employeeFullName}
      />
      <CardRow icon={<CalendarOutlined />} label="Date" value={formattedDate} />
      <CardRow
        icon={<ClockCircleOutlined />}
        label="Time"
        value={
          <>
            {shift.startTime} – {shift.endTime}
            <Tag style={{ marginLeft: 8 }}>{shift.durationInHours}h</Tag>
          </>
        }
      />
      <CardRow label="Type" value={<ShiftTypeTag type={shift.shiftType} />} />
      {shift.locationName && (
        <CardRow
          icon={<EnvironmentOutlined />}
          label="Location"
          value={shift.locationName}
        />
      )}
    </div>
  );
}

function MultiPreviewCard({ shift }: { shift: MultiShift }) {
  const startFormatted = formatDate(shift.startDate);
  return (
    <div className="vsm-card">
      <div className="vsm-card-badge">
        <Tag color="blue">Multiple shifts — {shift.timeFrame}</Tag>
      </div>
      <CardRow
        icon={<UserOutlined />}
        label="Employee"
        value={shift.employeeFullName}
      />
      <CardRow
        icon={<CalendarOutlined />}
        label="Starting"
        value={`${startFormatted} (1 ${shift.timeFrame})`}
      />
      <CardRow
        icon={<ClockCircleOutlined />}
        label="Time"
        value={
          <>
            {shift.startTime} – {shift.endTime}
            <Tag style={{ marginLeft: 8 }}>{shift.durationInHours}h</Tag>
          </>
        }
      />
      <CardRow label="Type" value={<ShiftTypeTag type={shift.shiftType} />} />
      {shift.locationName && (
        <CardRow
          icon={<EnvironmentOutlined />}
          label="Location"
          value={shift.locationName}
        />
      )}
    </div>
  );
}

function LeavePreviewCard({ shift }: { shift: LeaveShift }) {
  const startFormatted = formatDate(shift.startDate);
  const endFormatted =
    shift.leaveDates.length > 1
      ? formatDate(shift.leaveDates[shift.leaveDates.length - 1])
      : null;

  return (
    <div className="vsm-card">
      <div className="vsm-card-badge">
        <Tag color="orange">Leave shift</Tag>
      </div>
      <CardRow
        icon={<UserOutlined />}
        label="Employee"
        value={shift.employeeFullName}
      />
      <CardRow
        icon={<CalendarOutlined />}
        label="Period"
        value={
          endFormatted
            ? `${startFormatted} – ${endFormatted} (${shift.leaveDates.length} days)`
            : startFormatted
        }
      />
      <CardRow label="Leave type" value={shift.leaveTypeName} />
      <CardRow
        label="Pay"
        value={`${shift.leavePayPercentage}% · ${shift.leavePayType === "HOURS" ? "per hour" : "daily wage"}`}
      />
      {shift.locationName && (
        <CardRow
          icon={<EnvironmentOutlined />}
          label="Location"
          value={shift.locationName}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------
function CardRow({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="vsm-card-row">
      <span className="vsm-card-icon">{icon}</span>
      <span className="vsm-card-label">{label}</span>
      <span className="vsm-card-value">{value}</span>
    </div>
  );
}

const SHIFT_TYPE_COLORS: Record<string, string> = {
  DAILY_WAGE: "purple",
  TEMPORARY_HELP: "cyan",
  HOURS: "blue",
  LEAVES: "orange",
  "Morning Shift": "gold",
  "Afternoon Shift": "geekblue",
  "Night Shift": "volcano",
};

const SHIFT_TYPE_LABELS: Record<string, string> = {
  DAILY_WAGE: "Dnevnica",
  TEMPORARY_HELP: "Ispomoc",
  HOURS: "Sati",
  LEAVES: "Odsustvo",
  "Morning Shift": "Jutarnja",
  "Afternoon Shift": "Popodnevna",
  "Night Shift": "Noćna",
};

function ShiftTypeTag({ type }: { type: string }) {
  return (
    <Tag color={SHIFT_TYPE_COLORS[type] || "default"}>
      {SHIFT_TYPE_LABELS[type] || type}
    </Tag>
  );
}

function formatDate(dateStr: string): string {
  try {
    return format(parseISO(dateStr), "EEEE, MMM d, yyyy");
  } catch {
    return dateStr;
  }
}
